import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, asset, IMG } from "@/lib/api";
import CourseCard from "@/components/courses/CourseCard";
import { PageHeader } from "@/components/shared/SectionHeader";
import { Search, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ARTICLES, ARTICLE_CATEGORIES } from "@/content/articles";
import { PUBLIC_LAUNCH } from "@/constants/launch";

const TRACKS = ["All", "Ownership Academy", "Certification Track", "Breeder Academy", "Cognitive Development Academy", "Nutrition Academy", "Rescue Academy"];

const normalize = (s) => s.toLowerCase().replace(/\s+/g, " ");
const ARTICLE_INDEX = Object.fromEntries(
  ARTICLES.map(a => [a.slug, normalize([a.title, a.category, a.body.replace(/\*/g, "")].join(" "))])
);

export default function Learn() {
  const [courses, setCourses] = useState([]);
  const [track, setTrack] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => { if (PUBLIC_LAUNCH) return; api.get("/courses").then(r => setCourses(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    return courses.filter(c => (track === "All" || c.track === track) &&
      (q === "" || (c.title + " " + c.subtitle + " " + c.instructor).toLowerCase().includes(q.toLowerCase())));
  }, [courses, track, q]);

  const [aq, setAq] = useState("");
  const searchRef = useRef(null);
  const matches = useMemo(() => {
    const needle = normalize(aq).trim();
    return needle ? ARTICLES.filter(a => ARTICLE_INDEX[a.slug].includes(needle)) : ARTICLES;
  }, [aq]);

  return (
    <div>
      <PageHeader
        eyebrow="Learning Center"
        title="Learning Center"
        description="Public educational articles for prospective and current wolfdog owners."
        image={IMG.forest}
      />

      <section className="container-x pt-14" data-testid="learn-articles">
        <div className="eyebrow">Articles</div>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="w-full sm:w-96">
            <label htmlFor="article-search" className="block text-sm font-semibold mb-2">Search articles</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
              <input
                id="article-search"
                ref={searchRef}
                type="search"
                value={aq}
                onChange={e => setAq(e.target.value)}
                placeholder="Search article titles, topics, or text…"
                data-testid="article-search"
                className="w-full pl-9 pr-4 py-3 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          {aq && (
            <button
              type="button"
              onClick={() => { setAq(""); searchRef.current?.focus(); }}
              data-testid="article-search-clear"
              className="self-start sm:self-auto text-sm font-semibold px-3 py-3 rounded-md hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Clear search
            </button>
          )}
        </div>
        <p role="status" aria-live="polite" data-testid="article-search-count" className="mt-3 text-sm text-muted-foreground">
          {matches.length === 1 ? "1 article" : `${matches.length} articles`}
        </p>
        {matches.length === 0 && (
          <p data-testid="article-search-empty" className="mt-6 text-muted-foreground">No articles match your search. Try another word.</p>
        )}
        {ARTICLE_CATEGORIES.filter(cat => matches.some(a => a.category === cat)).map(cat => (
        <div key={cat} className="mt-6" data-testid={`article-category-${cat.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
        <h2 className="font-display text-2xl md:text-3xl tracking-tight">{cat}</h2>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {matches.filter(a => a.category === cat).map(a => (
            <div key={a.slug} className="rounded-lg border border-border bg-card p-6" data-testid={`article-card-${a.slug}`}>
              <div className="eyebrow !normal-case text-xs" data-testid="article-card-category">{a.category}</div>
              <h3 className="font-display text-2xl leading-tight mt-2 text-balance">{a.title}</h3>
              <Link
                to={`/learn/articles/${a.slug}`}
                data-testid={`article-read-${a.slug}`}
                className="mt-4 inline-flex items-center gap-1 text-sm font-semibold hover:underline rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                Read Article <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          ))}
        </div>
        </div>
        ))}
      </section>

      {!PUBLIC_LAUNCH && (
      <section className="container-x py-14">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            {TRACKS.map(t => (
              <button
                key={t}
                onClick={() => setTrack(t)}
                data-testid={`learn-filter-${t.toLowerCase().replace(/\s+/g,'-')}`}
                className={
                  "text-xs uppercase tracking-[0.2em] font-semibold px-3 py-2 rounded-full border transition " +
                  (track === t ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-foreground/40")
                }
              >
                {t}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search courses, instructors…"
              data-testid="learn-search"
              className="w-full pl-9 pr-4 py-3 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(c => <CourseCard key={c.id} c={c} />)}
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-24 text-muted-foreground">No courses match those filters.</div>
        )}
      </section>
      )}

    </div>
  );
}
